import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, inject, Component, NgModule, importProvidersFrom } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, filter, Subject, take, map as map$1, finalize, of, forkJoin } from 'rxjs';
import * as i1$1 from '@ngx-translate/core';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import * as i1 from '@studiohyperdrive/ngx-core';
import { scan, map, shareReplay, catchError, tap } from 'rxjs/operators';
import { UUID } from 'angular2-uuid';
import { HttpClient, HttpBackend } from '@angular/common/http';
import { deepmerge } from 'deepmerge-ts';

class NgxI18nAbstractService {
}

const NgxI18nConfigurationToken = new InjectionToken('NgxI18nConfigurationToken');

//TODO: Iben: Once we have a shared lib we should replace the storage and the browser approaches with their corresponding implementations
class NgxI18nRootService {
    constructor(configuration, windowsService) {
        this.configuration = configuration;
        this.windowsService = windowsService;
        /**
         * A subject to hold the current language
         */
        this.currentLanguageSubject = new BehaviorSubject(undefined);
        /**
         * A subject to hold the available languages
         */
        this.availableLanguagesSubject = new BehaviorSubject([]);
        /**
         * The available languages
         *
         * Only emits once the list contains at least one language
         */
        this.availableLanguages$ = this.availableLanguagesSubject
            .asObservable()
            .pipe(filter((languages) => languages?.length > 0));
        /**
         * The current language of the application, as an Observable
         */
        this.currentLanguage$ = this.currentLanguageSubject.asObservable();
        // Iben: Set the initial values so that we can refer to the services as the source of truth
        this.defaultLanguage = configuration.defaultLanguage;
        this.languageRouteParam = configuration.languageRouteParam || 'language';
        this.availableLanguagesSubject.next(this.configuration.availableLanguages || []);
    }
    /**
     * The current language of the application
     */
    get currentLanguage() {
        return this.currentLanguageSubject.getValue();
    }
    /**
     * Sets the current language of the application and saves it to the local storage. Returns true if the language was set
     *
     * @param language - The provided language
     */
    setCurrentLanguage(language) {
        // Iben: get the new language
        const newLanguage = this.getNewLanguage(language);
        // Iben: Save the current language to the localStorage when we're in the browser
        this.windowsService.runInBrowser(() => {
            localStorage.setItem('ngx-i18n-language', newLanguage);
        });
        // Iben: Update the subject
        this.currentLanguageSubject.next(newLanguage);
    }
    /**
     * Sets the initial language of the application when no language is set yet.
     *
     * If a previous language was set in the local storage, said language is used. If not, the default language gets used.
     */
    initializeLanguage() {
        // Iben: If the current language already exists, we early exit
        if (this.currentLanguage) {
            return;
        }
        // Iben: If the current language does not exist, we check if it exists in the local storage, if not, we use the default config
        let language = this.defaultLanguage;
        this.windowsService.runInBrowser(() => {
            language = localStorage.getItem('ngx-i18n-language') || this.defaultLanguage;
        });
        // Iben: We set the new language
        this.setCurrentLanguage(language);
    }
    /**
     * Set the list of available languages
     *
     * @param languages - The list of available languages
     */
    setAvailableLanguages(languages) {
        this.availableLanguagesSubject.next(languages);
    }
    /**
     * Checks if the newly proposed language can be set, if not we return either the current language or the default language
     *
     * @param {string} language - The newly proposed language
     */
    getNewLanguage(language) {
        // Iben: Save the currently being set language
        let newLanguage = language;
        // Iben: Check if the new language is part of the available languages
        if (!this.availableLanguagesSubject.getValue().includes(language)) {
            // Iben: If a language is set that's not part of the available languages, we return a warn
            console.warn(`NgxI18n: A language, ${language}, was attempted to be set that was not part of the available languages (${this.availableLanguagesSubject
                .getValue()
                .join(', ')})`);
            // Iben: If there is already a language set, we early exit and keep the remaining language
            if (this.currentLanguage) {
                return this.currentLanguage;
            }
            // Iben: If no language exists, we use the default language
            newLanguage = this.defaultLanguage;
        }
        return newLanguage;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxI18nRootService, deps: [{ token: NgxI18nConfigurationToken }, { token: i1.NgxWindowService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxI18nRootService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxI18nRootService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NgxI18nConfigurationToken]
                }] }, { type: i1.NgxWindowService }] });

class NgxI18nService {
    constructor(translateService, rootI18nService) {
        this.translateService = translateService;
        this.rootI18nService = rootI18nService;
        /**
         * Set the current language of the application
         *
         * @param language - The provided language
         */
        this.setLanguage = (language) => {
            this.rootI18nService.setCurrentLanguage(language);
            this.translateService.use(this.rootI18nService.currentLanguage);
        };
    }
    /**
     * Returns the current language of the application
     */
    get currentLanguage() {
        return this.rootI18nService.currentLanguage;
    }
    /**
     * Returns the available languages of the application
     */
    get availableLanguages() {
        return this.translateService.langs;
    }
    /**
     * Returns the default language of the application
     */
    get defaultLanguage() {
        return this.translateService.getDefaultLang();
    }
    /**
     * Initializes the translations based on the provided language
     *
     * @param language - The provided language
     */
    initI18n(language) {
        // Iben: If the language is provided, set it in the root service
        if (language) {
            this.rootI18nService.setCurrentLanguage(language);
        }
        this.translateService.use(this.rootI18nService.currentLanguage);
        return this.translateService.reloadLang(language);
    }
    /**
     * Returns an instant translation based on a provided key and params
     *
     * @param key - The key of the translation
     * @param params - An optional set of params
     */
    getTranslation(key, params) {
        return this.translateService.instant(key, params);
    }
    // Denis: Set the default to string but allow for others.
    /**
     * Returns a observable based translation based on a provided key and params
     *
     * @param key - The key of the translation
     * @param params - An optional set of params
     */
    getTranslationObservable(key, params) {
        return this.translateService.get(key, params);
    }
    /**
     * Returns the currently loaded translations
     */
    get translations() {
        return this.translateService.translations;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxI18nService, deps: [{ token: i1$1.TranslateService }, { token: NgxI18nRootService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxI18nService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxI18nService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i1$1.TranslateService }, { type: NgxI18nRootService }] });

class NgxI18nLoadingService {
    constructor() {
        // Iben: Keep a subject to store all the translation loading actions
        this.translationLoaderActionsSubject$ = new Subject();
        /**
         * A subject to store all the currently loaded translation files in
         */
        this.translationsSubject$ = new BehaviorSubject({});
        this.translationsFailedSubject$ = new BehaviorSubject(false);
        this.translationsLoading = {};
        // Iben: Check if all translations actions have completed
        this.translationsLoaded$ = this.translationLoaderActionsSubject$.pipe(
        // Iben: Hold a list of all actions and only add those that have a loading state
        // If the action is not loading, remove it from the actions array
        scan((actions, action) => {
            return action.state === 'LOADING'
                ? actions.concat(action.id)
                : actions.filter((actionFromResult) => actionFromResult !== action.id);
        }, []), 
        // Iben: If no more actions are loading, we return true
        map((result) => result.length === 0));
        this.translationsFailed$ = this.translationsFailedSubject$.asObservable();
    }
    /**
     * Dispatch the loading state of a set of translations
     *
     * @param action - The loading state of a set of translations
     */
    dispatchTranslationLoaderAction(action) {
        this.translationLoaderActionsSubject$.next(action);
    }
    /**
     * Add loaded translations to the loaded translations record
     *
     * @param translations - The newly loaded translations
     */
    addLoadedTranslations(translations) {
        this.translationsSubject$.next({
            ...this.translationsSubject$.getValue(),
            ...translations,
        });
    }
    /**
     * Fetches all the currently loaded translations
     */
    getTranslations() {
        return this.translationsSubject$.getValue();
    }
    /**
     * Groups all the requests for a series of paths and ensures we only have a single observable to subscribe to
     *
     * @param paths - The paths of we're loading translations for
     * @param observable - The translations loading observable
     */
    loadTranslations(paths, observable) {
        // Iben: If loading of the translations has not been added to the array yet, we set it so we return a single observable
        if (!this.translationsLoading[paths]) {
            // Iben: Add shareReplay so the result is passed over each subscription
            this.translationsLoading[paths] = observable.pipe(shareReplay());
        }
        // Iben: Return the observable
        return this.translationsLoading[paths];
    }
    /**
     * Mark the loading of the translations as failed
     */
    markTranslationsLoadedAsFailed() {
        console.error('Something went wrong whilst fetching the translations.');
        this.translationsFailedSubject$.next(false);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxI18nLoadingService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxI18nLoadingService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxI18nLoadingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

var TranslationLoaderActionStateEnum;
(function (TranslationLoaderActionStateEnum) {
    TranslationLoaderActionStateEnum["LOADING"] = "LOADING";
    TranslationLoaderActionStateEnum["LOADED"] = "LOADED";
})(TranslationLoaderActionStateEnum || (TranslationLoaderActionStateEnum = {}));

/**
 * Set the language in the url of the route
 *
 * @param route - The provided route
 */
const NgxI18nGuard = (route) => {
    // Iben: Fetch all injectables
    const router = inject(Router);
    const i18nService = inject(NgxI18nRootService);
    // Iben: Initialize the current language of the application
    i18nService.initializeLanguage();
    return i18nService.availableLanguages$.pipe(take(1), map$1((availableLanguages) => {
        // Iben: Get the two language params
        const currentLanguage = i18nService.currentLanguage;
        const routeLanguage = getLanguage(route, availableLanguages, i18nService.languageRouteParam);
        // Iben: If both languages are the same, we can continue
        if (currentLanguage === routeLanguage) {
            return true;
        }
        // Iben: If the router language differs, we check if it is available
        if (availableLanguages.includes(routeLanguage)) {
            // Iben: Update the language
            i18nService.setCurrentLanguage(routeLanguage);
            //Iben: Re-route to the new language
            router.navigate(['/', routeLanguage]);
            return true;
        }
        //Iben: The current language is set to "default" when no previous language exists.
        router.navigate(['/', currentLanguage]);
        return false;
    }));
};
/**
 * Fetches the language from the route
 *
 * @param route - The provided route
 * @param config - The provided config
 */
const getLanguage = (route, availableLanguages, languageRouteParam) => {
    const language = route?.paramMap.get(languageRouteParam || 'language');
    const parent = route?.parent;
    if (!parent && !language) {
        return undefined;
    }
    if (availableLanguages.includes(language)) {
        return language;
    }
    return getLanguage(parent, availableLanguages, languageRouteParam);
};

/**
 * This NgxI18nTranslationLoaderResolver will make sure that the translations are loaded from the moment you route to a detail module
 * It is essential you put this resolver at the root of your modules, whether it be lazy loaded through the guard or calling the resolver in the constructor
 */
class NgxI18nTranslationLoaderResolver {
    constructor(i18nService, i18nLoadingService) {
        this.i18nService = i18nService;
        this.i18nLoadingService = i18nLoadingService;
    }
    resolve() {
        // Iben: Generate an id for the translations we're about to load
        const id = UUID.UUID();
        // Iben: Dispatch to the loader service that we're about to load a set of translations
        this.i18nLoadingService.dispatchTranslationLoaderAction({ id, state: 'LOADING' });
        // Iben: This will reload the translations, and fetch the newly needed lazy loaded translations
        return this.i18nService.initI18n(this.i18nService.currentLanguage).pipe(
        // Iben: Map to true for the guard
        map$1(() => true), finalize(() => {
            // Iben: Dispatch to the loader service that the translations have been loaded
            this.i18nLoadingService.dispatchTranslationLoaderAction({ id, state: 'LOADED' });
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxI18nTranslationLoaderResolver, deps: [{ token: NgxI18nService }, { token: NgxI18nLoadingService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxI18nTranslationLoaderResolver }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxI18nTranslationLoaderResolver, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: NgxI18nService }, { type: NgxI18nLoadingService }] });

/**
 * Loads in all translations for a specific module
 */
const NgxI18nTranslationLoaderGuard = () => {
    // Iben: Fetch all injectables
    const loader = inject(NgxI18nTranslationLoaderResolver);
    if (!loader) {
        return false;
    }
    return loader.resolve();
};

/**
 * Set the language in the url of the route
 *
 * @param route - The provided route
 */
const NgxI18nSetLanguageGuard = () => {
    // Iben: Fetch all injectables
    const router = inject(Router);
    const i18nService = inject(NgxI18nRootService);
    // Iben: Initialize the current language of the application
    i18nService.initializeLanguage();
    //Iben: Navigate to the set currentLanguage
    return router.navigate(['/', i18nService.currentLanguage]);
};

class NgxI18nMultiTranslationHttpLoader {
    constructor(httpBackend, translationsPaths) {
        this.httpBackend = httpBackend;
        this.translationsPaths = translationsPaths;
        this.translationLoadingService = inject(NgxI18nLoadingService);
        /**
         * The configuration for the NgxI18nModule.
         */
        this.config = inject(NgxI18nConfigurationToken);
    }
    /**
     * Fetches the provided translation files and saves them to the translation store
     *
     * @param  lang - The currently used language
     */
    getTranslation(lang) {
        // Iben: Fetch the currently existing translations, so we can see if they already exist in the loading service
        const availableTranslations = this.translationLoadingService.getTranslations();
        // Iben: Convert the translation paths to a combination of calls to get the translations or from the translations store
        const requestedTranslations = this.translationsPaths.map((path) => {
            const fromStore = Boolean(availableTranslations[path]);
            if (fromStore) {
                // Iben: If the translations came from the store, mark them as such
                return of({
                    translations: availableTranslations[path],
                    fromStore,
                    path,
                });
            }
            else {
                // Iben: If the translations aren't available in the store, we fetch them from the server
                // Wouter: When provided, add a cache busting param so that each request is fetched from the server instead of the browser cache
                const fetchPath = `${path}${lang}.json${this.config.cacheBust ? '?v=' + this.config.cacheBust : ''}`;
                return new HttpClient(this.httpBackend).get(fetchPath).pipe(
                // Iben: Map this to an object so we can track which results corresponds with which path
                map((translations) => {
                    return {
                        translations,
                        path,
                        fromStore,
                    };
                }), 
                // Iben: In case the translation is not loaded, we log an error
                catchError((res) => {
                    console.error('Something went wrong for the following translation file:', fetchPath);
                    console.error(res.message);
                    // Iben: If we get a not-authorized error from the server, we redirect to the resources not found page
                    if (res.status === 401) {
                        this.translationLoadingService.markTranslationsLoadedAsFailed();
                    }
                    // Iben: Return a translation loaded object so the translations service isn't broken
                    return of({
                        translations: {},
                        path,
                        fromStore,
                    });
                }));
            }
        });
        // Iben: Join all the requests and merge them
        return this.translationLoadingService.loadTranslations(this.translationsPaths.toString(), forkJoin(requestedTranslations).pipe(tap((translations) => {
            // Iben: Filter out the newly requested translations, and add them to the loaded translations store
            this.translationLoadingService.addLoadedTranslations(translations
                .filter((translation) => !translation.fromStore)
                .reduce((previous, next) => {
                return {
                    ...previous,
                    [next.path]: next.translations,
                };
            }, {}));
        }), 
        // Iben: Merge all the translations in a single object for the translation service
        map((response) => deepmerge(...response.map((item) => item.translations)))));
    }
}

/**
 * This is an empty dummy component that can be used in combination with the NgxI18nSetLanguageGuard when needed
 */
class NgxI18nEmptyComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxI18nEmptyComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: NgxI18nEmptyComponent, isStandalone: true, selector: "ngx-i18n-empty", ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxI18nEmptyComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ngx-i18n-empty',
                    standalone: true,
                    template: '',
                }]
        }] });

function FallBackTranslationLoader(http) {
    // Iben: Inject the config
    const config = inject(NgxI18nConfigurationToken);
    // Iben: Return a default loader
    return new NgxI18nMultiTranslationHttpLoader(http, config.defaultAssetPaths || []);
}
class NgxI18nModule {
    static forRoot(config, translationLoader) {
        const providers = [
            ...TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: translationLoader || FallBackTranslationLoader,
                    deps: [HttpBackend],
                },
                useDefaultLang: true,
            }).providers,
            {
                provide: NgxI18nConfigurationToken,
                useValue: config,
            },
            NgxI18nService,
        ];
        return {
            ngModule: NgxI18nModule,
            providers,
        };
    }
    static forChild(translationLoader) {
        const providers = [
            ...TranslateModule.forChild({
                loader: {
                    provide: TranslateLoader,
                    useFactory: translationLoader || FallBackTranslationLoader,
                    deps: [HttpBackend],
                },
                isolate: true,
            }).providers,
            NgxI18nService,
            NgxI18nTranslationLoaderResolver,
        ];
        return {
            ngModule: NgxI18nModule,
            providers,
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxI18nModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.0.3", ngImport: i0, type: NgxI18nModule, imports: [TranslateModule], exports: [TranslateModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxI18nModule, imports: [TranslateModule, TranslateModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxI18nModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [TranslateModule],
                    exports: [TranslateModule],
                }]
        }] });

/**
 * Returns the root providers for the NgxI18nModule
 *
 * @param config - The configuration for the NgxI18nModule
 * @param translationLoader - An optional translation loader
 */
const importNgxI18nProviders = (config, translationLoader) => {
    // Iben: Return the providers from the module
    return [importProvidersFrom(NgxI18nModule.forRoot(config, translationLoader))];
};
/**
 * Returns a route with the provided translations
 *
 * @param route - The route we wish to provide with translations
 * @param translationLoader - An optional translation loader
 */
const provideWithTranslations = (route, translationLoader) => {
    // Iben: Grab the existing route and extend the providers with the providers from the module
    return {
        ...route,
        providers: [
            ...(route.providers || []),
            importProvidersFrom(NgxI18nModule.forChild(translationLoader)),
        ],
    };
};

/*
 * Public API Surface of i18n
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NgxI18nAbstractService, NgxI18nConfigurationToken, NgxI18nEmptyComponent, NgxI18nGuard, NgxI18nLoadingService, NgxI18nModule, NgxI18nMultiTranslationHttpLoader, NgxI18nRootService, NgxI18nService, NgxI18nSetLanguageGuard, NgxI18nTranslationLoaderGuard, NgxI18nTranslationLoaderResolver, importNgxI18nProviders, provideWithTranslations };
